import type { ColorConfig } from '@tempo-io/roadmap-dto';
import chroma from 'chroma-js';
import type { ItemV2 } from '@/lib/api/types/items';
import { getItemFieldValue } from './fieldExtraction';

export type ColorPalette = {
  id: string;
  label: string;
  headerColors: string[];
  itemColors: string[];
};

export type PaletteSwatches = {
  headerColors: string[];
  itemColors: string[];
};

/**
 * Default color constants
 */
export const DEFAULT_BASE_COLOR = '#9664FE';
export const DEFAULT_PALETTE_ID = 'tempo-suite';
export const DEFAULT_FALLBACK_COLOR = '#D4D4D7';

/**
 * Predefined color palettes
 * First 12 palettes mirror the loop-2026 prototype (tempo-io/prototype-playground
 * src/components/roadmap-timeline/timeline-palettes.ts). The first 5 itemColors
 * match the prototype's swatches exactly; the remaining 7 extend the family at
 * similar saturation. headerColors reuse the same swatches.
 */
export const PREDEFINED_PALETTES: ColorPalette[] = [
  {
    id: 'citrus',
    label: 'Wild Harvest',
    headerColors: ['#166534', '#CA8A04', '#EA580C', '#DC2626', '#7C3AED'],
    itemColors: [
      '#166534',
      '#CA8A04',
      '#EA580C',
      '#DC2626',
      '#7C3AED',
      '#15803D',
      '#A16207',
      '#9A3412',
      '#991B1B',
      '#5B21B6',
      '#65A30D',
      '#B45309',
    ],
  },
  {
    id: 'groovy',
    label: 'Vivid Bloom',
    headerColors: ['#DC2626', '#EC4899', '#2563EB', '#F97316', '#C2410C'],
    itemColors: [
      '#DC2626',
      '#EC4899',
      '#2563EB',
      '#F97316',
      '#C2410C',
      '#BE185D',
      '#1D4ED8',
      '#9A3412',
      '#DB2777',
      '#1E40AF',
      '#EA580C',
      '#831843',
    ],
  },
  {
    id: 'pastel',
    label: 'Soft Pastel',
    headerColors: ['#FBCFE8', '#DDD6FE', '#FEF08A', '#BBF7D0', '#BFDBFE'],
    itemColors: [
      '#FBCFE8',
      '#DDD6FE',
      '#FEF08A',
      '#BBF7D0',
      '#BFDBFE',
      '#FED7AA',
      '#FECACA',
      '#E9D5FF',
      '#A7F3D0',
      '#FDE68A',
      '#C7D2FE',
      '#F5D0FE',
    ],
  },
  {
    id: 'autumn',
    label: 'Autumn Foliage',
    headerColors: ['#1E3A5F', '#3B82C4', '#991B1B', '#EA580C', '#D97706'],
    itemColors: [
      '#1E3A5F',
      '#3B82C4',
      '#991B1B',
      '#EA580C',
      '#D97706',
      '#1E40AF',
      '#7C2D12',
      '#B45309',
      '#0C4A6E',
      '#9A3412',
      '#A16207',
      '#0369A1',
    ],
  },
  {
    id: 'neon',
    label: 'Neon Lights',
    headerColors: ['#DB2777', '#F472B6', '#EAB308', '#84CC16', '#D9F99D'],
    itemColors: [
      '#DB2777',
      '#F472B6',
      '#EAB308',
      '#84CC16',
      '#D9F99D',
      '#BE185D',
      '#EC4899',
      '#CA8A04',
      '#65A30D',
      '#BEF264',
      '#A21CAF',
      '#FBBF24',
    ],
  },
  {
    id: 'ocean',
    label: 'Open Ocean',
    headerColors: ['#0C4A6E', '#0284C7', '#22D3EE', '#FEF9C3', '#FFFBEB'],
    itemColors: [
      '#0C4A6E',
      '#0284C7',
      '#22D3EE',
      '#FEF9C3',
      '#FFFBEB',
      '#075985',
      '#0369A1',
      '#06B6D4',
      '#67E8F9',
      '#FEF08A',
      '#0E7490',
      '#FDE68A',
    ],
  },
  {
    id: 'mountain-views',
    label: 'Misty Highland',
    headerColors: ['#57534E', '#3F6212', '#65A30D', '#BEF264', '#ECFCCB'],
    itemColors: [
      '#57534E',
      '#3F6212',
      '#65A30D',
      '#BEF264',
      '#ECFCCB',
      '#44403C',
      '#4D7C0F',
      '#84CC16',
      '#D9F99D',
      '#78716C',
      '#365314',
      '#A3A3A3',
    ],
  },
  {
    id: 'aegean-seaside',
    label: 'Aegean Seaside',
    headerColors: ['#1E40AF', '#3B82F6', '#1E3A8A', '#14532D', '#A21CAF'],
    itemColors: [
      '#1E40AF',
      '#3B82F6',
      '#1E3A8A',
      '#14532D',
      '#A21CAF',
      '#1D4ED8',
      '#60A5FA',
      '#172554',
      '#166534',
      '#86198F',
      '#2563EB',
      '#15803D',
    ],
  },
  {
    id: 'endless-summer',
    label: 'Juniper Mist',
    headerColors: ['#B6A6D3', '#C88E6B', '#C2D1B2', '#C0827C', '#494426'],
    itemColors: [
      '#B6A6D3',
      '#C88E6B',
      '#C2D1B2',
      '#C0827C',
      '#494426',
      '#A7C4DE',
      '#1E3A5F',
      '#3B82C4',
      '#D2C1B0',
      '#BC8B83',
      '#A89BB8',
      '#9CAE85',
    ],
  },
  {
    id: 'desert-skies',
    label: 'Desert Skies',
    headerColors: ['#DC2626', '#C026D3', '#F472B6', '#FB923C', '#EAB308'],
    itemColors: [
      '#DC2626',
      '#C026D3',
      '#F472B6',
      '#FB923C',
      '#EAB308',
      '#991B1B',
      '#A21CAF',
      '#EC4899',
      '#EA580C',
      '#CA8A04',
      '#BE185D',
      '#F97316',
    ],
  },
  {
    id: 'nordic-harbour',
    label: 'Quiet Harbour',
    headerColors: ['#78350F', '#A16207', '#EAB308', '#7DD3FC', '#FED7AA'],
    itemColors: [
      '#78350F',
      '#A16207',
      '#EAB308',
      '#7DD3FC',
      '#FED7AA',
      '#92400E',
      '#B45309',
      '#CA8A04',
      '#38BDF8',
      '#FDBA74',
      '#0EA5E9',
      '#FCD34D',
    ],
  },
  {
    id: 'tempo-suite',
    label: 'Tempo Suite',
    headerColors: ['#EA580C', '#7C3AED', '#EAB308', '#0D9488', '#FDBA74'],
    itemColors: [
      '#EA580C',
      '#7C3AED',
      '#EAB308',
      '#0D9488',
      '#FDBA74',
      '#C2410C',
      '#5B21B6',
      '#CA8A04',
      '#0F766E',
      '#FB923C',
      '#6D28D9',
      '#14B8A6',
    ],
  },
  {
    id: 'toy-store',
    label: 'Carnival Ride',
    headerColors: ['#1D4ED8', '#0891B2', '#7C3AED', '#DB2777', '#F59E0B'],
    itemColors: [
      '#1D4ED8',
      '#0891B2',
      '#7C3AED',
      '#DB2777',
      '#F59E0B',
      '#2563EB',
      '#06B6D4',
      '#A21CAF',
      '#BE185D',
      '#EA580C',
      '#0EA5E9',
      '#CA8A04',
    ],
  },
  {
    id: 'electric-avenue',
    label: 'Electric Avenue',
    headerColors: ['#171717', '#1E293B', '#CA8A04', '#DC2626', '#84CC16'],
    itemColors: [
      '#171717',
      '#1E293B',
      '#CA8A04',
      '#DC2626',
      '#84CC16',
      '#404040',
      '#334155',
      '#EAB308',
      '#991B1B',
      '#65A30D',
      '#525252',
      '#475569',
    ],
  },
  {
    id: 'midnight-metropolis',
    label: 'Midnight Metropolis',
    headerColors: ['#171717', '#1E293B', '#0D9488', '#A16207', '#BE185D'],
    itemColors: [
      '#171717',
      '#1E293B',
      '#0D9488',
      '#A16207',
      '#BE185D',
      '#262626',
      '#334155',
      '#0F766E',
      '#CA8A04',
      '#9D174D',
      '#404040',
      '#A21CAF',
    ],
  },
  {
    id: 'laurentian-foliage',
    label: 'Sunlit Orchard',
    headerColors: ['#15803D', '#BE185D', '#EA580C', '#CA8A04', '#7C2D12'],
    itemColors: [
      '#15803D',
      '#BE185D',
      '#EA580C',
      '#CA8A04',
      '#7C2D12',
      '#166534',
      '#9D174D',
      '#F97316',
      '#A16207',
      '#9A3412',
      '#65A30D',
      '#DB2777',
    ],
  },
];

function getDefaultPalette(): ColorPalette {
  const palette = PREDEFINED_PALETTES.find((p) => p.id === DEFAULT_PALETTE_ID);
  if (palette) {
    return palette;
  }

  const fallback = PREDEFINED_PALETTES[0];
  if (!fallback) {
    throw new Error('No default palette available');
  }

  return fallback;
}

/**
 * Generate monochromatic color shades from a base color
 * Matches Roadmunk's implementation using monochromatic palette generation
 * @param baseColor - Hex color string (e.g., '#9664FE')
 * @returns Array of hex color strings (10 shades)
 */
export function getBasePaletteColors(baseColor: string): string[] {
  try {
    // Generate 6 monochromatic shades (darkest to lightest)
    const palette = chroma
      .scale([chroma(baseColor).darken(2), baseColor, chroma(baseColor).brighten(2)])
      .mode('lab')
      .colors(6);

    // Add inverse set to avoid sudden color jump (similar to Roadmunk)
    // This creates a smoother transition: [dark -> light -> dark]
    const inversePalette = palette.slice(1, palette.length - 1).reverse();
    const fullPalette = [...palette, ...inversePalette];

    return fullPalette;
  } catch {
    // Fallback to Tempo Suite if base color is invalid
    return getDefaultPalette().itemColors;
  }
}

/**
 * Get a predefined palette by ID
 * @param paletteId - ID of the palette (e.g., 'tempo-suite')
 * @returns ColorPalette object
 */
export function getStaticPalette(paletteId: string): ColorPalette {
  const palette = PREDEFINED_PALETTES.find((p) => p.id === paletteId);

  if (!palette) {
    // Fallback to Tempo Suite if palette not found
    return getDefaultPalette();
  }

  return palette;
}

function getDefaultSwatches(): PaletteSwatches {
  const defaultPalette = getDefaultPalette();

  return {
    headerColors: defaultPalette.headerColors.length
      ? defaultPalette.headerColors
      : defaultPalette.itemColors,
    itemColors: defaultPalette.itemColors,
  };
}

function resolvePaletteSwatches(palette: ColorPalette): PaletteSwatches {
  return {
    headerColors: palette.headerColors.length ? palette.headerColors : palette.itemColors,
    itemColors: palette.itemColors,
  };
}

/**
 * Get both header and item swatches based on colorConfig
 */
export function getPaletteSwatches(colorConfig: ColorConfig | null | undefined): PaletteSwatches {
  if (!colorConfig) {
    return getDefaultSwatches();
  }

  // Base color mode: use generated palette for both header and item colours
  if (colorConfig.mode === 'baseColor' && colorConfig.baseColor) {
    const generated = getBasePaletteColors(colorConfig.baseColor);
    return {
      headerColors: generated,
      itemColors: generated,
    };
  }

  // Palette mode: use predefined palette swatches
  if (colorConfig.mode === 'palette' && colorConfig.paletteId) {
    return resolvePaletteSwatches(getStaticPalette(colorConfig.paletteId));
  }

  return getDefaultSwatches();
}

export function getPaletteHeaderColors(colorConfig: ColorConfig | null | undefined): string[] {
  return getPaletteSwatches(colorConfig).headerColors;
}

/**
 * Get palette colors from colorConfig
 * Handles both base color mode and palette mode
 * @param colorConfig - Color configuration from layout
 * @returns Array of hex color strings
 */
export function getPaletteColors(colorConfig: ColorConfig | null | undefined): string[] {
  return getPaletteSwatches(colorConfig).itemColors;
}

function getLuminance(color: string): number {
  try {
    const c = chroma(color);
    const alpha = c.alpha();
    if (alpha < 1) {
      const [r, g, b] = c.rgb();
      const blendedR = alpha * r + (1 - alpha) * 255;
      const blendedG = alpha * g + (1 - alpha) * 255;
      const blendedB = alpha * b + (1 - alpha) * 255;
      return chroma(blendedR, blendedG, blendedB).luminance();
    }
    return c.luminance();
  } catch {
    return 0;
  }
}

/**
 * Generate a mapping of field values to color indices
 * This determines which color from the palette should be used for each field value
 * @param items - Array of items to extract field values from
 * @param fieldId - The field ID to use for color mapping (e.g., 'status', 'team', or custom field ID)
 * @param orderedHeaders - Optional array of headers in display order. If provided, maps field values to their position in this array instead of alphabetical order
 * @returns Record mapping field values to color indices
 */
export function generateItemColorMap(
  items: ItemV2[],
  fieldId: string,
  orderedHeaders?: Array<{ id: string }>,
): Record<string, number> {
  // If ordered headers are provided, use their order for color mapping
  // This ensures colors match the user-defined header ordering
  if (orderedHeaders && orderedHeaders.length > 0) {
    const colorMap: Record<string, number> = {};
    orderedHeaders.forEach((header, index) => {
      colorMap[header.id] = index;
    });
    return colorMap;
  }

  // Otherwise, extract unique values from items and sort alphabetically
  const uniqueValues = new Set<string>();
  let hasNoValue = false;

  items.forEach((item) => {
    const fieldValue = getItemFieldValue(item, fieldId);
    if (fieldValue !== undefined) {
      uniqueValues.add(fieldValue);
    } else {
      hasNoValue = true;
    }
  });

  // Sort values alphabetically for consistent ordering
  // IMPORTANT: Filter out 'null' (No Value) before sorting to ensure consistent indices
  const sortedValues = Array.from(uniqueValues).sort();

  if (hasNoValue && !sortedValues.includes('null')) {
    sortedValues.push('null');
  }

  // Map each value to a color index
  const colorMap: Record<string, number> = {};
  sortedValues.forEach((value, index) => {
    colorMap[value] = index;
  });

  return colorMap;
}

/**
 * Determine if text should be black or white based on background color
 * Returns '#000000' for light backgrounds, '#FFFFFF' for dark backgrounds
 */
export function getContrastTextColor(backgroundColor: string): string {
  const luminance = getLuminance(backgroundColor);
  // Use black text if luminance is above 0.5 (light background)
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export const SUBHEADER_OPACITY = 28;
export const VALID_DROP_TARGET_OPACITY = SUBHEADER_OPACITY * 3;

/**
 * Apply opacity to a hex color and return as rgba CSS string
 */
export function applyOpacity(hexColor: string, opacity: number): string {
  return chroma(hexColor).alpha(opacity).css();
}
